import { useCallback, useEffect, useState } from 'react';
import ReactLoading from 'react-loading';
import { useSearchParams } from 'react-router-dom';
import EmptyPage from '../../empty-page/emptyPage';
import Pagination from '../../pagination/Pagination';
import { orgStayListingsFun, orgStayReviewsFun } from '../../../api/orgCampAPI';
import errorHandle from '../../../utils/errorHandle';
import { day } from '../orgStay/stayColumns';
import styles from '../orgStay/orgStay.module.css';

/*
 * Organizer > Camp > Reviews: what guests wrote about your stays (newest first)
 * and the average rating.
 */
const Stars = ({ value }) => (
    <span className={styles.stars} aria-label={`${value} out of 5`}>
        {'★★★★★'.slice(0, Math.round(value))}
        <span className={styles.starsOff}>{'★★★★★'.slice(Math.round(value))}</span>
    </span>
);

const OrgCampReviews = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const listingId = searchParams.get('stay') || '';
    const pageNumber = Number(searchParams.get('page')) || 1;
    const limit = 10;
    const offSet = (pageNumber - 1) * limit;
    const [listings, setListings] = useState([]);
    const [data, setData] = useState({ data: [], totalCount: 0, average: 0 });
    const [loading, setLoading] = useState(false);

    const updateURL = useCallback(
        (params) => {
            const current = { ...Object.fromEntries([...searchParams]), ...params };
            Object.keys(current).forEach((k) => current[k] === '' && delete current[k]);
            setSearchParams(current);
        },
        [searchParams, setSearchParams]
    );

    useEffect(() => {
        orgStayListingsFun()
            .then((res) => setListings(res?.data || []))
            .catch(errorHandle);
    }, []);

    useEffect(() => {
        let gone = false;
        setLoading(true);
        orgStayReviewsFun({ listingId: listingId || undefined, limit, offset: offSet })
            .then((res) => !gone && setData(res?.data || {}))
            .catch((error) => !gone && errorHandle(error))
            .finally(() => !gone && setLoading(false));
        return () => {
            gone = true;
        };
    }, [listingId, offSet]);

    const reviews = data.data || [];

    return (
        <div className='dashboard'>
            {loading && (
                <div className='loadingBackground'>
                    <ReactLoading type='spin' color='#006aff' className='loadingSpin' />
                </div>
            )}
            <div className={styles.reviewHead}>
                <div className={styles.average}>
                    <strong>{data.average ? data.average.toFixed(1) : '—'}</strong>
                    <div>
                        <Stars value={data.average || 0} />
                        <div className={styles.muted}>
                            {data.totalCount || 0} review{data.totalCount === 1 ? '' : 's'}
                        </div>
                    </div>
                </div>
                <select
                    className='select'
                    aria-label='Stay'
                    value={listingId}
                    onChange={(e) => updateURL({ stay: e.target.value, page: 1 })}
                >
                    <option value=''>All stays</option>
                    {listings.map((l) => (
                        <option key={l._id} value={l._id}>
                            {l.title}
                        </option>
                    ))}
                </select>
            </div>
            {reviews.length === 0 && !loading ? (
                <EmptyPage />
            ) : (
                <div className={styles.reviewList}>
                    {reviews.map((r) => (
                        <article key={r._id} className={styles.review}>
                            <div className={styles.reviewTop}>
                                <strong>{r.userName}</strong>
                                <Stars value={r.rating} />
                                <span className={styles.muted}>{day(r.createdAt)}</span>
                            </div>
                            <div className={styles.muted}>{r.listingId?.title}</div>
                            {r.comment && <p className={styles.comment}>{r.comment}</p>}
                        </article>
                    ))}
                    <Pagination
                        paginationClick={(n) => Math.ceil((data.totalCount || 0) / limit) > 1 && updateURL({ page: n })}
                        numberOfRows={data.totalCount || 0}
                        offSet={offSet}
                        pageNumber={pageNumber}
                        limit={limit}
                    />
                </div>
            )}
        </div>
    );
};

export default OrgCampReviews;
